"use client";

// The dashboard — now the Internet OS. The projects section is a directory you
// descend; the other root sections (you · activity · parked · connections ·
// agents · settings) are panels rendered inside the same shell. This file keeps those
// panels (the written-to-be-read content) and hands everything else — header,
// breadcrumb, navigator, organism, dock, gestures — to OsShell.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import OsShell from "../../components/os/OsShell";
import { createClient } from "../../lib/supabase/client";
import { maxDate, type OsNode, type RootAgent, type RootProject, type PanelKind, type PanelMeta } from "../../lib/os/tree";
import { type ObState } from "../../lib/os/onboarding";
import DirectoryView, { type ContextAction } from "../../components/os/DirectoryView";
import { useOsLayout } from "../../lib/os/useOsLayout";
import { symbolFor } from "../../lib/os/symbols";
import { useOsShell } from "../../components/os/OsShellContext";
import RevealObserver from "../../components/RevealObserver";
import MovedInArrival from "../../components/intake/MovedInArrival";
import { GroundSeed } from "../../components/ThemeProvider";
import type { TaskItem } from "../../components/os/TaskListView";
import { type Insight, type MemoryHealth } from "../../lib/os/insights";
import { computeCells } from "../../lib/usage/cells";
import HeroUploader from "../../components/brand/HeroUploader";
import NettiDock from "../../components/netti/NettiDock";
import ShareCardHost from "../../components/sharecard/ShareCardHost";
import AccessSheetHost from "../../components/you/AccessSheetHost";
import RingWatch from "../../components/inbox/RingWatch";
import { canSee, F, type FeatureGate, type Viewer } from "../../lib/features/registry";
import { nettiIsHere } from "../../components/connect/connectAsk";
import AccountIdentity from "../../components/dashboard/AccountIdentity";
import AvatarNudge from "../../components/dashboard/AvatarNudge";
import SaveHandedSkill from "../../components/dashboard/SaveHandedSkill";
import dynamic from "next/dynamic";
import { warmByUsage, whenIdle } from "../../lib/os/usage";
import { rearmable } from "../../components/os/SafeBoundary";
import { retryImport, wellFormedRows } from "../../lib/os/resilience";
import { reachQuietly } from "../../lib/os/outage";
import { startPoll } from "../../lib/os/poll";
import { warmInOrder } from "../../lib/os/warm";
import NavChips from "../../components/os/NavChips";
import { ReleaseProvider } from "../../lib/release/next";
import { NETTI_PANEL_VIEW_KEY, arrivalMoved, arrivalView, movedNettiView, retiredDeskTab, retiredIdentityTab } from "../../components/os/nettiViews";
import { AGENTS_PANEL_VIEW_KEY, arrivalHomeView } from "../../components/agents/agentViews";
import { claimResumable, personKey, useResumable } from "../../lib/os/resume";
import { PLACES } from "../../lib/os/places";

// law_snappy — every panel is its own chunk. Only the one you open mounts, so
// only the ones you open download: the panels THIS person visits are warmed while
// idle (the shell notes each visit, lib/os/usage), and a navigator word warms its
// panel while the pointer rests on it, so the open that follows finds it ready.
// (import() stays inline in each dynamic(); the loaders below share its chunk.)
// Each panel is rearmable: React.lazy remembers a rejected import for the life of
// the page, so a panel whose code failed to download (a dropped connection, a
// tab left open across a deploy) used to stay dead however often "open it again"
// was pressed. `load` retries the download twice, and if it still fails the next
// retry from the panel's boundary gets a fresh lazy (see rearmable in SafeBoundary).
// …and each shows a quiet skeleton while its code is on the way (PanelSkeleton),
// so the stage is never an empty room between the press and the panel.
const YouSpace = rearmable((load) => dynamic(() => load(() => import("../../components/you/YouSpace")), { loading: PanelSkeleton }));
const NettiSpace = rearmable((load) => dynamic(() => load(() => import("../../components/os/NettiSpace")), { loading: PanelSkeleton }));
const SkillsSpace = rearmable((load) => dynamic(() => load(() => import("../../components/os/SkillsSpace")), { loading: PanelSkeleton }));
const ActivitySpace = rearmable((load) => dynamic(() => load(() => import("../../components/activity/ActivitySpace")), { loading: PanelSkeleton }));
const OursPanel = rearmable((load) => dynamic(() => load(() => import("../../components/ours/OursPanel")), { loading: PanelSkeleton }));
const TasksSpace = rearmable((load) => dynamic(() => load(() => import("../../components/dashboard/TasksSpace")), { loading: PanelSkeleton }));
const ConnectionsPanel = rearmable((load) => dynamic(() => load(() => import("../../components/dashboard/ConnectionsPanel")), { loading: PanelSkeleton }));
// beta (F.agents): mounted only when data.agentsEnabled, so a person it is not open to never downloads it
const AgentsSpace = rearmable((load) => dynamic(() => load(() => import("../../components/agents/AgentsSpace")), { loading: PanelSkeleton }));
// today's connections and agents spaces, for everyone who is not a test account (lib/release/next)
const ConnectionsPanelV1 = rearmable((load) => dynamic(() => load(() => import("../../components/dashboard/ConnectionsPanel.v1")), { loading: PanelSkeleton }));
const AccountPanel = rearmable((load) => dynamic(() => load(() => import("../../components/dashboard/AccountPanel")), { loading: PanelSkeleton }));
const AppSettings = rearmable((load) => dynamic(() => load(() => import("../../components/dashboard/AppSettings")), { loading: PanelSkeleton }));
const ChatSettings = rearmable((load) => dynamic(() => load(() => import("../../components/dashboard/ChatSettings")), { loading: PanelSkeleton }));
// the daily digest letter: whether it comes and when it lands (/api/me/digest)
const DigestSettings = rearmable((load) => dynamic(() => load(() => import("../../components/dashboard/DigestSettings")), { loading: PanelSkeleton }));
// founders only while it is tried (F.agentIdentity): the agents space's identities view, mounted only when it is open
const IdentitySettings = rearmable((load) => dynamic(() => load(() => import("../../components/dashboard/IdentitySettings")), { loading: PanelSkeleton }));
// founders only (F.nettiDesk), so it is never in PANEL_LOAD's warm set below. It is the body of the
// netti space's "on your mac" view, handed in as this already-lazy slot: it downloads only when that
// view opens, never with the space
const DeskSettings = rearmable((load) => dynamic(() => load(() => import("../../components/dashboard/DeskSettings")), { loading: PanelSkeleton }));
// the memory space (/memory): the whole memory's health, and the home of "import your memory"
const MemorySpace = rearmable((load) => dynamic(() => load(() => import("../../components/dashboard/MemorySpace")), { loading: PanelSkeleton }));
// (the welcome overlay shows nothing of its own while its code is on the way)
const OnboardingFlow = rearmable((load) => dynamic(() => load(() => import("../../components/onboarding/OnboardingFlow")), { loading: Quiet }));
// the warm-up loaders retry a dropped download too, so a hover that finds the
// network blinking still leaves the panel ready by the time it is opened
const PANEL_LOAD: Record<string, () => Promise<unknown>> = {
  you: () => retryImport(() => import("../../components/you/YouSpace")),
  memory: () => retryImport(() => import("../../components/dashboard/MemorySpace")),
  netti: () => retryImport(() => import("../../components/os/NettiSpace")),
  skills: () => retryImport(() => import("../../components/os/SkillsSpace")),
  activity: () => retryImport(() => import("../../components/activity/ActivitySpace")),
  ours: () => retryImport(() => import("../../components/ours/OursPanel")),
  tasks: () => retryImport(() => import("../../components/dashboard/TasksSpace")),
  connections: () => retryImport(() => (RELEASE_NEXT ? import("../../components/dashboard/ConnectionsPanel") : import("../../components/dashboard/ConnectionsPanel.v1"))),
  // the agents space is one space on every release (2026-10-25): today's kept them in connections
  agents: () => retryImport(() => import("../../components/agents/AgentsSpace")),
  settings: () => retryImport(() => Promise.all([import("../../components/dashboard/AccountPanel"), import("../../components/dashboard/AppSettings")])),
};

// which release the warm set loads for (set once per page, from the loader's word, before any warm runs)
let RELEASE_NEXT = false;

// The main rooms, warmed for EVERYONE once the dashboard has settled (the usage
// ledger only knows what a person has opened before, which for a brand-new
// account is nothing): in the order a first visit is likely to reach them.
const WARM_FOR_ALL = ["you", "memory", "connections", "activity", "netti", "skills"] as const;

// A boundary of its own with nothing in it: next/dynamic only puts a Suspense
// boundary around a part when it is given a `loading`, and without one a chunk
// that is late suspends hydration of everything above it (see Quiet in OsShell).
function Quiet() {
  return null;
}

// What the stage shows while a panel's code arrives: a title's worth of shimmer
// and a few quiet lines on the ground, in the folder skeleton's own rhythm (it
// holds back ~120ms, so a warm panel swaps straight in without a flash).
function PanelSkeleton() {
  return (
    <div
      className="os-arriving"
      aria-busy="true"
      aria-label="opening"
      style={{ flexDirection: "column", flexWrap: "nowrap", alignItems: "flex-start", gap: "1.1rem", maxWidth: 520 }}
    >
      <span className="skeleton-line" style={{ width: "34%", height: "1.5rem", opacity: 0.7 }} />
      <span className="skeleton-line" style={{ width: "88%", height: "0.8em", opacity: 0.5 }} />
      <span className="skeleton-line" style={{ width: "72%", height: "0.8em", opacity: 0.45 }} />
      <span className="skeleton-line" style={{ width: "58%", height: "0.8em", opacity: 0.4 }} />
    </div>
  );
}

/** the chips of the settings space, in the order they stand */
type SettingsTab = "account" | "app" | "digest" | "chats" | "security" | "data";
const SETTINGS_TABS: readonly SettingsTab[] = ["account", "app", "digest", "chats", "security", "data"];

export interface DashProject {
  id: string;
  slug: string;
  name: string;
  type: string;
  tagline: string | null;
  dimCount: number;
  nodeCount: number;
  head: string | null;
  updated_at: string;
  /** per-project folder colour / icon overrides (optional — older callers and
   *  the dev harness omit them; absent reads as "auto"). */
  color?: string | null;
  icon?: string | null;
  /** the project this nests inside (null = top level). Load-bearing: without it
   *  buildRoot treats every project as top-level, so a nested subproject shows up
   *  BOTH at /projects AND inside its parent — a duplicate id that overlaps. */
  parentId?: string | null;
  /** a code project (lib/os/codeProject): it wears the repo mark and opens a repo view. */
  code?: boolean;
  /** how many of its direct children are code projects (a parent's repo view grid). */
  codeChildren?: number;
}
export interface DashParked {
  id: string;
  project_id: string;
  name: string;
  summary: string | null;
  created_at: string;
  project_name?: string;
}
export interface DashSession {
  id: string;
  /** EXACT oauth_clients.client_id — the Connections panel matches on this.
   *  Optional only so the os-harness sample (which omits it) still type-checks;
   *  the real loader always sets it (= the client_id). */
  client_id?: string;
  client_name: string;
  created_at: string;
  scope?: string | null;
  last_used_at?: string | null;
}
export interface DashConnector {
  id: string;
  type: string;
  last_synced_at: string | null;
  sync_status: string;
}
export interface DashActivity {
  id: string;
  scope: string;
  summary: string;
  title?: string | null;
  context?: string | null;
  source: string | null;
  link: string | null;
  created_at: string;
  account?: string | null;
  /** the row's own entity (a project name / slug / target) — set by the loader
   *  (data.ts) and read by ActivityFeed.parse. Optional. */
  project?: string | null;
  /** delivery lifecycle — 'ok' (default) | 'queued' | 'failed' | 'stale'. */
  status?: string | null;
}
export interface DashFact {
  dimension: string;
  statement: string;
}
export interface DashSpaceMember {
  userId: string;
  role: string;
  name: string | null;
  avatarUrl: string | null;
}
export interface DashSpace {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  iconUrl: string | null;
  ownerId: string;
  createdAt: string;
  members: DashSpaceMember[];
  linkCount: number;
}
export interface DashIntent {
  intent: string;
  ambient_off: boolean;
  active_project: string | null;
}
export interface DashboardData {
  email: string;
  showArchived: boolean;
  projects: DashProject[];
  parked: DashParked[];
  /** parked (archived) projects, RootProject-shaped with parked:true — appended to
   *  the projects array so buildRoot routes them into the /parked directory.
   *  Optional so the os-harness sample still type-checks. */
  parkedProjects?: RootProject[];
  sessions: DashSession[];
  activity: DashActivity[];
  facts: DashFact[];
  /** total CURRENT personal facts (uncapped count) — for the cells metric. The
   *  `facts` array above is capped at 40 + non-private (for the ticker only). */
  factsCount: number;
  intent: DashIntent | null;
  isAdmin?: boolean;
  /** the rituals belong to ONE person — not to the founders. See lib/rituals/caller. */
  ritualsFounder?: boolean;
  /** which release this person is on (lib/release/next): `next` for a test account (lib/testers/accounts) */
  release?: "now" | "next";
  /** registry feature ids granted to this person (the beta module, migration 066). */
  betaFeatures?: string[];
  /** the console's overlay (feature_gates, migration 157): rows moved to testers or opened by the launch press */
  featureGates?: Record<string, FeatureGate>;
  insights: Insight[];
  health: MemoryHealth;
  /** '/projects' (home) or a panel path like '/dev' for ?panel= deep-links. */
  initialPath: string;
  /** THE STAGE: navigator in flow, content in its own scroller, a URL per place. */
  stage?: boolean;
  /** where the stage's addresses live (`/dashboard`, `/mirror`). */
  base?: string;
  /** the place the address names (`/projects/p1/vision`), stage only. */
  place?: string | null;
  /** the view chip the address names (`?v=`), stage only. */
  initialView?: string | null;
  /** how the stage holds the split (`?fold=split` compares the literal split). */
  stageFold?: "layer" | "split";
  /** a page or cell to open on arrival (`?open=`), e.g. from a forget preview. */
  initialOpen?: string | null;
  initialOpenView?: string | null;
  /** preselected sub-view for the connections panel (?view=api). */
  connectionsView?: "connect" | "tools" | "apps" | "agents" | "mcp" | "npx" | "api" | "extensions" | "vault";
  /** the settings tab a ?panel=settings&view= link named, read on the server: the shell
   *  rewrites the address to /settings before the tab could read it from there */
  settingsView?: string;
  usage: { mcpCalls30d: number; lastActivity: string | null };
  /** external mirror connectors (Notion, …) the Connections panel manages.
   *  Optional fields default safely so the os-harness sample still type-checks;
   *  the real dashboard loader always sets all three. */
  connectors?: DashConnector[];
  /** brand-new account with nothing on yet → Connections opens in arrival mode. */
  firstRun?: boolean;
  /** whether user_metadata.onboarded is already set. */
  onboarded?: boolean;
  /** the in-dashboard onboarding "odyssey" state (user_settings.onboarding), or null
   *  when there's no in-flight flow (a graduated or pre-existing account). Drives the
   *  OnboardingConductor + space locking. */
  onboarding?: ObState | null;
  /** open (not-Done) task count across all projects — drives the tasks-space nav chip. */
  taskCount?: number;
  /** dev/preview only (os-harness): seed the tasks space in-memory, no network. */
  demoTasks?: TaskItem[];
  /** beta gate — the tasks space + per-project tasks chip only render when true. */
  tasksEnabled?: boolean;
  /** founder gate — the shared-spaces "ours" section renders when true. */
  oursEnabled?: boolean;
  /** beta gate (F.agents) — the agents section renders when true: founders, and anyone
   *  granted it in feature_access. Required, so a caller cannot forget to say. */
  agentsEnabled: boolean;
  /** how many agents this person keeps. null = not read (the space is closed to them,
   *  or migration 146 is not applied yet): the navigator then shows no figure. */
  agentsCount: number | null;
  /** every agent this person keeps, top-level and sub-agents (the columns the shell's
   *  tree needs, nothing more): each becomes a real path under /agents from the first
   *  paint, so a reload or a ?agent= link lands inside one. [] when the space is
   *  closed to them or the table is not there yet. Optional so the os-harness sample
   *  and the developers page still type-check; the real loader always sets it. */
  agentRows?: RootAgent[];
  /** the caller's profile (display name + uploaded avatar) — post-063; optional
   *  so the os-harness sample still type-checks. */
  profile?: { displayName: string | null; avatarUrl: string | null };
  /** the caller's shared spaces (ours) — members carried for the rings. */
  spaces?: DashSpace[];
  /** dev/preview only (os-harness): a pre-built project subtree spliced into the
   *  tree so a folder's content (e.g. artifacts) renders without a network fetch. */
  initialSubtree?: OsNode;
  /** the account's saved ground (user_settings.ground; a minted one for a new
   *  person), rendered into the HTML so the first paint is already theirs.
   *  `demo`: the mirror's pretend person, painted but never saved. */
  ground?: { bgId: string; rgb: [number, number, number]; demo?: boolean } | null;
  /** netti's tours this person has seen (user_settings.tours, 154) */
  toursSeen?: Record<string, string>;
  /** when the account was made */
  joinedAt?: string | null;
}

const LABEL: React.CSSProperties = { fontSize: "0.72rem", letterSpacing: "0.04em", color: "var(--ink-3)" };
const dimName = (d: string) => d.replace(/[_-]/g, " ");

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ color: "var(--ink-3)", fontSize: "0.92rem", lineHeight: 1.65, maxWidth: 480 }}>{children}</div>;
}

export default function DashboardClient({ data }: { data: DashboardData }) {
  // the name YOU chose is the name the whole shell says — the header cell, the
  // you space, netti's greeting, the feedback note. The email front is only ever
  // the fallback for an account that hasn't named itself yet.
  const userName = data.profile?.displayName?.trim() || data.email.split("@")[0];
  // the places kept in this browser are this person's: what another person (or nobody) left is forgotten before
  // anything reads it. Once, on this first render, which comes before every effect that takes a place back
  useState(() => {
    if (data.email) claimResumable(personKey(data.email));
  });
  // THE NEXT RELEASE (lib/release/next): a test account sees the v1.1 spaces, everyone else today's
  const next = data.release === "next";
  RELEASE_NEXT = next;
  const ConnectionsSpace = next ? ConnectionsPanel : ConnectionsPanelV1;

  // FIRST-RUN ONBOARDING — a brand-new account (firstRun, not yet onboarded)
  // opens straight into the full-screen OnboardingFlow overlay, above the shell.
  // On complete OR skip it POSTs /api/onboarding/complete (the same markOnboarded
  // path the Connections panel uses) and dismisses so the normal shell shows.
  // The new in-dashboard "odyssey" (data.onboarding set, behind ONBOARDING_V2)
  // fully replaces the legacy full-screen overlay — never show both.
  const [showOnboarding, setShowOnboarding] = useState(
    (data.firstRun ?? false) && !(data.onboarded ?? false) && !data.onboarding,
  );

  // LIVE PROJECTS — the dashboard reflects a project write the instant it lands,
  // wherever it came from (the CLI `innernet connect`/`push`, a save from another
  // tab, an agent). We subscribe to this user's `projects` rows; on any change we
  // refetch the authoritative root list (with accurate domain/node counts) and
  // hand it to both the home glance-grid and the shell. A new project mounts with
  // the os-enter fade-in and the organism fires (OsShell folds it in + pulses).
  // Realtime must be enabled on `projects` server-side (migration 038) — until
  // then this is a harmless no-op and the SSR list still shows on reload.
  const [liveProjects, setLiveProjects] = useState<DashProject[]>(data.projects);
  // keep in sync if the server payload itself changes (e.g. a soft nav re-fetch).
  useEffect(() => { setLiveProjects(data.projects); }, [data.projects]);
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    let disposed = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let channel: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let supabase: any = null;

    // resolves whether the read worked: the poll below backs off while it doesn't
    const refetch = async (): Promise<boolean> => {
      try {
        // reachQuietly: a deadline on the read, and a 401 is noticed (the shell
        // says so once) instead of the list quietly never updating again
        const res = await reachQuietly("/api/os/roots", { cache: "no-store" });
        if (disposed) return true;
        if (!res?.ok) return false;
        // a 200 that is not a list (a proxy's html, an error object) used to throw
        // inside the state updater below, which is a render crash of the whole
        // shell. Anything but a list changes nothing: the last good state stands.
        const roots = wellFormedRows<{
          id: string; slug: string; name: string; type: string;
          tagline: string | null; dimCount: number; nodeCount: number; updated_at: string;
          parentId?: string | null; color?: string | null; icon?: string | null;
          code?: boolean; codeChildren?: number;
        }>(await res.json());
        if (disposed) return true;
        if (!roots) return false;
        setLiveProjects((prev) => {
          const headById = new Map(prev.map((p) => [p.id, p.head] as const));
          const next = roots.map((r) => ({
            id: r.id, slug: r.slug, name: r.name, type: r.type, tagline: r.tagline,
            dimCount: r.dimCount, nodeCount: r.nodeCount,
            head: headById.get(r.id) ?? null, updated_at: r.updated_at,
            // the nesting + appearance ride along: dropping them here turned a
            // subproject into a top-level one the first time a poll changed.
            parentId: r.parentId ?? null, color: r.color ?? null, icon: r.icon ?? null,
            // …and so does the code flag, or the repo mark would blink off on a poll.
            code: !!r.code, codeChildren: r.codeChildren ?? 0,
          }));
          // law_snappy — a poll that finds nothing new must change NOTHING. A
          // fresh array every 30s re-rendered the whole shell, the open panel and
          // both [projects] effects while you were in the middle of something.
          const same =
            next.length === prev.length &&
            next.every((n, i) => {
              const p = prev[i];
              return p.id === n.id && p.slug === n.slug && p.name === n.name && p.type === n.type &&
                p.tagline === n.tagline && p.dimCount === n.dimCount && p.nodeCount === n.nodeCount &&
                p.updated_at === n.updated_at && p.head === n.head &&
                (p.parentId ?? null) === n.parentId && (p.color ?? null) === n.color && (p.icon ?? null) === n.icon &&
                !!p.code === n.code && (p.codeChildren ?? 0) === n.codeChildren;
            });
          return same ? prev : next;
        });
        return true;
      } catch {
        return false; /* offline / no env — keep what we have */
      }
    };

    // coalesce a burst of row changes into a single refetch.
    const onChange = () => {
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(() => void refetch(), 250);
    };

    (async () => {
      try {
        supabase = createClient();
        const { data: u } = await supabase.auth.getUser();
        const uid = u.user?.id;
        if (disposed || !uid) return;
        channel = supabase
          .channel(`os-projects-${uid}`)
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "projects", filter: `user_id=eq.${uid}` },
            onChange,
          )
          .subscribe();
      } catch { /* signed out / no realtime — SSR list stands */ }
    })();

    // THE SAFETY NET. Realtime on `projects` is the fast path, but it has
    // failed quietly before (a first folder made over MCP during the walk did
    // not show until a manual refresh). So the root list is ALSO re-read on a
    // cadence — fast while the walk is on (the folder it is waiting for), slow
    // forever after. The poll (lib/os/poll) is silent while the tab is hidden or
    // the network is gone, reads at once when either returns, and backs off to a
    // minute while the server answers badly. A poll that finds nothing new
    // changes nothing (same ids, same heads).
    const walking = !!data.onboarding && !(data.onboarded ?? false);
    const stopPoll = startPoll({ every: walking ? 6000 : 30000, run: refetch });

    return () => {
      disposed = true;
      stopPoll();
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
      if (channel && supabase) {
        try { supabase.removeChannel(channel); } catch { /* ignore */ }
      }
    };
  }, []);

  // warm the panels this person visits, once the dashboard has settled; and any
  // panel whose navigator word the pointer rests on. The main rooms are warmed
  // for EVERYONE too (lib/os/warm): a new account has no usage to go on, and its
  // first press on each word should not wait on a download.
  useEffect(() => {
    let stopWarm: (() => void) | null = null;
    const t = setTimeout(() => {
      whenIdle(() => warmByUsage(Object.fromEntries(Object.entries(PANEL_LOAD).map(([k, f]) => [`panel:${k}`, f]))));
      stopWarm = warmInOrder(PANEL_LOAD, WARM_FOR_ALL);
    }, 3000);
    const warmed = new Set<string>();
    const onOver = (e: PointerEvent) => {
      const key = (e.target as Element | null)?.closest?.("[data-nav-key]")?.getAttribute("data-nav-key");
      if (!key || warmed.has(key) || !PANEL_LOAD[key]) return;
      warmed.add(key);
      PANEL_LOAD[key]().catch(() => warmed.delete(key));
    };
    document.addEventListener("pointerover", onOver, { passive: true });
    return () => { clearTimeout(t); stopWarm?.(); document.removeEventListener("pointerover", onOver); };
  }, []);

  // the settings sub-tab is kept in this browser (lib/os/places): settings opens where it was left. Declared
  // above the effect below, so a link, a bridge or netti asking for a tab still wins over the kept one.
  const [keptTab, setSettingsTab] = useResumable<SettingsTab>(PLACES.settingsTab(), "account");
  // …and a kept tab this person cannot see (digest before the v1.1 press) is never the one shown
  const settingsTab: SettingsTab = (next ? SETTINGS_TABS : SETTINGS_TABS.filter((t) => t !== "digest")).includes(keptTab) ? keptTab : "account";
  // netti on the desk: the Mac app's corner, founders only for now
  // (law_nothing_in_the_works_goes_public). Its settings are the netti space's
  // "on your mac" view, shown only inside the app. Known on the server, so the
  // chip row renders the same before and after hydration and never jumps.
  // who is looking, once: the founder bit, their grants and the console's overlay, for every gate below
  const viewer: Viewer = useMemo(
    () => ({ isAdmin: data.isAdmin, betaFeatures: data.betaFeatures, gates: data.featureGates }),
    [data.isAdmin, data.betaFeatures, data.featureGates],
  );
  const deskOpen = canSee(F.nettiDesk, viewer);
  // agent identity: how your ai talks, answers, behaves and the habits it keeps (founders, F.agentIdentity).
  // It lives in the agents space's identities view (25 oct; it was the netti space's identity view before);
  // settings → agent identity, and every old pick of the netti one, hand it there. The view is a room of the
  // agents space, so it needs that space too (F.agents): nobody is handed a chip, a link or a room that is
  // not theirs. The agents space is the same on every release since 2026-10-25 (John: "move agents into its
  // own space … all the agents' control inside there"), so a grant opens it on today's release too.
  const identityOpen = canSee(F.agentIdentity, viewer) && data.agentsEnabled;
  // moving in (MOVING-IN-PLAN.md, F.moveIn): the sheet the link an ai hands back opens
  const moveInOpen = canSee(F.moveIn, viewer);
  // netti can open a settings tab ("where do i change my password"): its UI bridge
  // leaves the view in sessionStorage for a tab that has not been looked at yet and
  // announces it on `innernet:panel-view` for one that has. Only a real tab id is
  // ever taken, and the key is cleared once read so it never reopens by itself.
  // settings → netti on the desk used to be a tab here; a link to it (an app built
  // before the move, a bookmark) is handed to the netti space's "on your mac" view
  // for someone who can see it, and lands on app for anyone else.
  useEffect(() => {
    const ids: readonly SettingsTab[] = next ? ["account", "app", "digest", "chats", "security", "data"] : ["account", "app", "chats", "security", "data"];
    const key = "innernet:panel-view:settings";
    let handOff: ReturnType<typeof setTimeout> | null = null;
    const take = (v: unknown) => {
      // settings → agent identity moved to the netti space's identity view (20 oct) and on to the agents space's
      // identities view (25 oct): a pick of it goes there
      if (v === "desk" || v === "identity") {
        const to = v === "desk" ? retiredDeskTab(deskOpen) : retiredIdentityTab(identityOpen);
        if (!to) return;
        if (to.panel === "settings") return setSettingsTab(to.view);
        try { sessionStorage.setItem(to.panel === "agents" ? AGENTS_PANEL_VIEW_KEY : NETTI_PANEL_VIEW_KEY, to.view); } catch { /* private mode: the event reaches a mounted space */ }
        window.dispatchEvent(new CustomEvent("innernet:panel-view", { detail: { panel: to.panel, view: to.view } }));
        // the old pick sends the shell to settings as its last act: ours comes after it
        if (handOff) clearTimeout(handOff);
        handOff = setTimeout(() => window.dispatchEvent(new CustomEvent("innernet:netti-nav", { detail: { path: `/${to.panel}` } })), 120);
        return;
      }
      if (typeof v !== "string" || !(ids as readonly string[]).includes(v)) return;
      setSettingsTab(v as SettingsTab);
    };
    const clear = () => { try { sessionStorage.removeItem(key); } catch { /* private mode */ } };
    // a link that names the tab: the daily digest letter's "change when it lands"
    // (/dashboard?panel=settings&view=digest). The whitelist above still decides.
    // The server read it (the address is /settings by now); the bar is the fallback.
    take(data.settingsView);
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("panel") === "settings" || /\/settings\/?$/.test(window.location.pathname)) take(sp.get("view"));
    } catch { /* the address bar is cosmetic here */ }
    try { take(sessionStorage.getItem(key)); } catch { /* private mode */ }
    clear();
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ panel?: string; view?: string }>).detail;
      if (d?.panel !== "settings") return;
      take(d.view);
      clear();
    };
    window.addEventListener("innernet:panel-view", on);
    return () => {
      window.removeEventListener("innernet:panel-view", on);
      if (handOff) clearTimeout(handOff);
    };
  }, [deskOpen, identityOpen, next]);
  // the address's ?v= for the netti place, taken once by the space (so a cold load of
  // /dashboard/netti?v=mac lands there with no race), then forgotten
  const [nettiArrival, setNettiArrival] = useState(() => arrivalView(data.place, data.initialView));
  // …and the agents space's own (/dashboard/agents?v=identities), for the identities chip. "identities" is only
  // ever taken by the space where agent identity is open to this person (agentViews takeHomeView).
  const [agentsArrival, setAgentsArrival] = useState(() => arrivalHomeView(data.place, data.initialView));

  // THE OLD HOME OF IDENTITIES (25 oct): the netti space had an `identity` view (chip "personalities") until it
  // moved to the agents space as "identities". Every way to ask for the old one still lands on the new one, for
  // whoever can see it, and is ignored for anyone else (the netti space then opens on its first view):
  //   · the address (/dashboard/netti?v=identity, /mirror/netti?v=identity), read once on arrival;
  //   · a pick left in the tab (the UI bridge's `innernet:panel-view:netti` = identity: netti's own links, a tour, a
  //     chat row, MovedIn's door) and its announcement on `innernet:panel-view`.
  // The hand-off is the one the settings tab takes above: the agents space's view is left in the tab for a space
  // that has not mounted and announced for one that has, and the shell is carried there as the pick's last act
  // (its own move to /netti comes first, hence the pause).
  useEffect(() => {
    let handOff: ReturnType<typeof setTimeout> | null = null;
    const carry = () => {
      try { sessionStorage.setItem(AGENTS_PANEL_VIEW_KEY, "identities"); } catch { /* private mode: the event reaches a mounted space */ }
      window.dispatchEvent(new CustomEvent("innernet:panel-view", { detail: { panel: "agents", view: "identities" } }));
      if (handOff) clearTimeout(handOff);
      handOff = setTimeout(() => window.dispatchEvent(new CustomEvent("innernet:netti-nav", { detail: { path: "/agents" } })), 120);
    };
    const clear = () => { try { sessionStorage.removeItem(NETTI_PANEL_VIEW_KEY); } catch { /* private mode */ } };
    // the address
    if (arrivalMoved(data.place, data.initialView, identityOpen)) carry();
    // a pick left in the tab before this page was there to hear it
    try {
      if (movedNettiView(sessionStorage.getItem(NETTI_PANEL_VIEW_KEY), identityOpen)) { clear(); carry(); }
    } catch { /* private mode */ }
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ panel?: string; view?: string }>).detail;
      if (d?.panel !== "netti" || !movedNettiView(d.view, identityOpen)) return;
      clear();
      carry();
    };
    window.addEventListener("innernet:panel-view", on);
    return () => {
      window.removeEventListener("innernet:panel-view", on);
      if (handOff) clearTimeout(handOff);
    };
    // the address is read once, on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityOpen]);

  // parked (archived) projects ride the same array flagged parked:true —
  // buildRoot routes them into the /parked directory, never under /projects.
  const parkedProjects = useMemo(() => data.parkedProjects ?? [], [data.parkedProjects]);
  // memoized so the shell's [projects] effects (live arrivals, reconcile) run
  // when the list actually changes, not on every render of this component.
  const projects: RootProject[] = useMemo(() => [
    ...liveProjects.map((p) => ({
      id: p.id,
      slug: p.slug,
      name: p.name,
      type: p.type,
      tagline: p.tagline,
      dimCount: p.dimCount,
      nodeCount: p.nodeCount,
      updated_at: p.updated_at,
      // carry the nesting so buildRoot routes a subproject inside its parent
      // instead of duplicating it at the top level.
      parentId: p.parentId ?? null,
      color: p.color ?? null,
      icon: p.icon ?? null,
      code: !!p.code,
      codeChildren: p.codeChildren ?? 0,
    })),
    ...parkedProjects,
  ], [liveProjects, parkedProjects]);

  // ---- you (your space) ----
  // Reads the REAL record on open (/api/you/signals): rhythm, tools, attention,
  // the said/did mirror, the loom — plus today's letter and its evidence-bound
  // insights when the daily pass has written them. The organism stays the shell's
  // single planted instance; the loom is woven around it (law_netti_fixed_center).
  const youPanel = (
    <YouSpace
      userName={userName}
      avatarUrl={data.profile?.avatarUrl ?? null}
      viewer={viewer}
    />
  );

  // ---- activity (river · repos) ----
  // the empty line is the river's only: the repos view can have content before
  // any activity has landed.
  const activityPanel = (
    <ActivitySpace
      initial={data.activity}
      projects={projects}
      viewer={viewer}
      empty={
        <Empty>nothing yet.</Empty>
      }
    />
  );

  // ---- parked (the empty state only — with anything resting, /parked becomes a
  // REAL directory in the tree: idea pages read as artifacts, parked projects as
  // folders you can open. buildRoot does that routing; this panel never shows then.)
  const parkedPanel = (
    <Empty>
      nothing parked. when an idea isn&apos;t ready, say <em style={{ color: "var(--ink)" }}>“park this idea”</em> in any connected AI — it rests here, out of the way but never lost.
    </Empty>
  );

  // ---- connections (the live source of truth + first-run onboarding) ----
  // memoized: the panel re-seeds its LIVE session list whenever this prop
  // changes, so a fresh .map() per parent render threw away every realtime
  // arrival (and re-ran its funnel effects) on each unrelated re-render.
  const connSessions = useMemo(
    () =>
      data.sessions.map((s) => ({
        client_id: s.client_id ?? s.id,
        client_name: s.client_name,
        created_at: s.created_at,
        scope: s.scope ?? null,
        last_used_at: s.last_used_at ?? null,
      })),
    [data.sessions],
  );
  const connectionsPanel = (
    <ConnectionsSpace
      sessions={connSessions}
      connectors={data.connectors ?? []}
      onboarded={data.onboarded ?? false}
      firstRun={data.firstRun ?? false}
      usage={data.usage}
      // never `connect` here for today's space: the loader drops it for anyone not on the next release
      initialView={data.connectionsView as never}
      accountEmail={data.email}
      accountName={userName}
      viewer={viewer}
    />
  );

  // CELLS — the single abstract usage metric (lib/usage/cells.ts). One cell = one
  // stored memory unit, counted identically across project memory (nodes +
  // dimensions) and personal memory (current self_facts). This is what drives the
  // organism's age. (Was a buggy `nodes + facts.length` that ignored dimensions
  // and capped facts at 40 — under-aging every organism.) Declared HERE because
  // the composed account tab reads it too — it must exist before settingsPanel.
  const cells = computeCells({
    nodes: liveProjects.reduce((s, p) => s + (p.nodeCount || 0), 0),
    dimensions: liveProjects.reduce((s, p) => s + (p.dimCount || 0), 0),
    selfFactsCurrent: data.factsCount,
  }).cells;

  // ---- settings (chip sub-views: account · app · daily digest · in your chats · security · data) ----
  // "account" is now COMPOSED — the cell, the display name, the address and the
  // ledger of what innernet holds, in one read. (The old split, a "profile" tab
  // for the photo + name and an "account" tab that showed only the email, made
  // you hunt for half of yourself.) The memory's health and "import your memory"
  // are a space of their own now (/memory, MemorySpace), not a tab here.
  const settingsTabs: ReadonlyArray<{ id: SettingsTab; label: string }> = [
    { id: "account", label: "account" },
    { id: "app", label: "app" },
    // the daily digest's own tab (when it lands) is the next release's
    ...(next ? [{ id: "digest" as const, label: "daily digest" }] : []),
    { id: "chats", label: "in your chats" },
    { id: "security", label: "security" },
    { id: "data", label: "data" },
  ];
  const acctSessions = data.sessions.map((s) => ({ id: s.id, client_name: s.client_name, created_at: s.created_at }));
  const settingsPanel = (
    <div data-noswipe style={{ display: "flex", flexDirection: "column", gap: "2.2rem", maxWidth: settingsTab === "chats" ? 1040 : 560 }}>
      <NavChips label="views of settings">
        {settingsTabs.map((t) => (
          <button key={t.id} className="chip" data-on={settingsTab === t.id} onClick={() => setSettingsTab(t.id)}>
            {t.label}
          </button>
        ))}
      </NavChips>

      {settingsTab === "account" && (
        <div className="view-fade">
          <AccountIdentity
            email={data.email}
            initialName={data.profile?.displayName ?? null}
            initialAvatarUrl={data.profile?.avatarUrl ?? null}
            cells={cells}
            projectCount={liveProjects.length}
            roomCount={(data.spaces ?? []).length}
            connectionCount={data.sessions.length}
            parkedCount={data.parked.length}
            lastActivity={data.usage.lastActivity}
          />
        </div>
      )}

      {settingsTab === "app" && (
        <div className="view-fade">
          <AppSettings viewer={viewer} />
        </div>
      )}

      {next && settingsTab === "digest" && (
        <div className="view-fade">
          <DigestSettings />
        </div>
      )}

      {settingsTab === "chats" && (
        <div className="view-fade">
          <ChatSettings />
        </div>
      )}

      {settingsTab === "security" && (
        <div className="view-fade">
          <AccountPanel initialSessions={acctSessions} section="security" />
        </div>
      )}

      {settingsTab === "data" && (
        <div className="view-fade">
          <AccountPanel initialSessions={acctSessions} section="data" />
        </div>
      )}
    </div>
  );

  // ---- skills (everything you keep that an AI can learn from) ----
  const skillsPanel = (
    <SkillsSpace
      ownerName={userName}
      ownerAvatar={data.profile?.avatarUrl ?? null}
      viewer={viewer}
    />
  );

  // ---- tasks (cross-project running checklist, sortable by project) ----
  const tasksPanel = <TasksSpace projects={liveProjects.map((p) => ({ id: p.id, name: p.name }))} demoTasks={data.demoTasks} founder={data.ritualsFounder} />;

  const panels: Partial<Record<PanelKind, React.ReactNode>> = {
    you: youPanel,
    memory: <MemorySpace />,
    netti: (
      <NettiSpace
        cells={cells}
        projectCount={liveProjects.length}
        factCount={data.factsCount}
        isAdmin={data.isAdmin}
        userName={userName}
        deskOpen={deskOpen}
        deskView={deskOpen ? <DeskSettings /> : null}
        initialView={nettiArrival}
        onArrival={() => setNettiArrival(null)}
      />
    ),
    skills: skillsPanel,
    activity: activityPanel,
    ...(data.oursEnabled ? { ours: <OursPanel spaces={data.spaces ?? []} /> } : {}),
    ...(data.tasksEnabled ? { tasks: tasksPanel } : {}),
    ...(data.agentsEnabled ? {
      // its own space on every release: the orbit, the list, the activity, identities, and each agent's
      // page with its settings and its removal (today's release kept the home in connections until 10-25)
      agents: (
        <AgentsSpace
          userName={userName}
          avatarUrl={data.profile?.avatarUrl ?? null}
          identityOpen={identityOpen}
          identityView={identityOpen ? <IdentitySettings /> : null}
          initialView={agentsArrival}
          onArrival={() => setAgentsArrival(null)}
        />
      ),
    } : {}),
    parked: parkedPanel,
    connections: connectionsPanel,
    settings: settingsPanel,
  };

  // PANEL STATUS — give the data-bearing sections (you · connections · activity ·
  // parked) the SAME "N items ● lastActive" status chip the projects section has,
  // rendered in the navigator's consistent stat-line slot (not floating in content).
  // the you panel's status carries the unified cells count (the project size language, now used
  // everywhere) + last sync — so the total lives here, not duplicated in the panel.
  const lastConnUsed = data.sessions
    .map((s) => s.last_used_at || s.created_at)
    .filter(Boolean)
    .sort()
    .at(-1) as string | undefined;
  const panelMeta: PanelMeta = {
    you: {
      stats: [{ label: "cells", value: cells.toLocaleString() }],
      lastActiveAt: data.health.lastSyncAt,
    },
    memory: {
      stats: [{ label: "cells", value: cells.toLocaleString() }],
      lastActiveAt: data.health.lastSyncAt,
    },
    connections: {
      stats: [{ label: data.sessions.length === 1 ? "tool" : "tools", value: data.sessions.length }],
      lastActiveAt: lastConnUsed ?? null,
    },
    activity: {
      stats: [{ label: data.activity.length === 1 ? "event" : "events", value: data.activity.length }],
      lastActiveAt: data.activity[0]?.created_at ?? null,
    },
    ...(data.tasksEnabled ? {
      tasks: {
        stats: [{ label: (data.taskCount ?? 0) === 1 ? "task" : "tasks", value: data.taskCount ?? 0 }],
        lastActiveAt: null,
      },
    } : {}),
    ...(data.oursEnabled ? {
      ours: {
        stats: [{ label: (data.spaces?.length ?? 0) === 1 ? "space" : "spaces", value: data.spaces?.length ?? 0 }],
        lastActiveAt: null,
      },
    } : {}),
    // an unread count (null) is no figure at all: "0 agents" would say there are none
    ...(data.agentsEnabled ? {
      agents: {
        stats: data.agentsCount == null ? [] : [{ label: data.agentsCount === 1 ? "agent" : "agents", value: data.agentsCount }],
        lastActiveAt: null,
      },
    } : {}),
    parked: {
      // projects + ideas together — the one "parked" figure; the ideas themselves
      // ride along so buildRoot can seed the park's document pages.
      stats: [{ label: "parked", value: data.parked.length + parkedProjects.length }],
      lastActiveAt:
        maxDate(...data.parked.map((b) => b.created_at), ...parkedProjects.map((p) => p.updated_at)) ?? null,
      parkedIdeas: data.parked,
    },
  };

  return (
    <ReleaseProvider release={next ? "next" : "now"}>
      {/* the person's ground, in the HTML itself, before any script */}
      <GroundSeed ground={data.ground} />
      {/* the link an ai hands back when it has moved someone in (?moved=<run>): what moved in, once (F.moveIn) */}
      {moveInOpen && <MovedInArrival />}
      <OsShell
        projects={projects}
        initialPath={data.initialPath}
        stage={!!data.stage}
        base={data.base}
        initialPlace={data.place ?? null}
        initialView={data.initialView ?? null}
        stageFold={data.stageFold ?? "layer"}
        initialOpen={data.initialOpen ?? null}
        initialOpenView={data.initialOpenView ?? null}
        initialSubtree={data.initialSubtree}
        panels={panels}
        userName={userName}
        isAdmin={data.isAdmin}
        cells={cells}
        panelMeta={panelMeta}
        tasksEnabled={data.tasksEnabled}
        oursEnabled={data.oursEnabled}
        agentsEnabled={data.agentsEnabled}
        toursSeen={data.toursSeen ?? {}}
        joinedAt={data.joinedAt ?? null}
        betaFeatures={data.betaFeatures ?? []}
        featureGates={data.featureGates}
        agentRows={data.agentRows}
        // the rooms hang under `ours` so the ≡ menu drills into each one.
        spaces={(data.spaces ?? []).map((s) => ({
          id: s.id,
          slug: s.slug,
          name: s.name,
          tagline: s.tagline,
          memberCount: s.members.length,
          linkCount: s.linkCount,
          createdAt: s.createdAt,
        }))}
        avatarUrl={data.profile?.avatarUrl ?? null}
        onboarding={data.onboarding ?? null}
        connected={(data.sessions?.length ?? 0) > 0}
      />
      {/* keep this user's email hero fresh (their real organism, baked in-browser
          and uploaded) so branded emails show THEIR bloom. Renders nothing unless
          a fresh bake is due; entirely best-effort. */}
      <HeroUploader email={data.email} cells={cells} />

      {/* a skill someone handed you (`?add=skill:…` / `?add=share:…`) — asks
          which folder it lands in, saves it as it was kept, opens the folder. */}
      {!showOnboarding && <SaveHandedSkill projects={data.projects} />}

      {/* netti's corner — hover the bottom-right for the waiting cursor; click
          to open the collapsible conversation rail. Founders get the real
          conversation; everyone else gets netti saying, honestly, that it isn't
          public yet ("available soon"). Stays out of first-run; the guide CSS
          also retires it while netti is walking someone in. */}
      {/* ask netti: founders, until NEXT_PUBLIC_NETTI_CHAT_OPEN=1 or the console's launch press opens it (the ask route reads the same door) */}
      {!showOnboarding && <NettiDock preview={!nettiIsHere(viewer)} />}

      {/* one image of one thing innernet noticed about you — any surface calls
          openShareCard() (lib/sharecard/types); the studio loads on first use. */}
      <ShareCardHost
        enabled={canSee(F.shareCard, viewer)}
        person={{ name: userName, avatarUrl: data.profile?.avatarUrl ?? null }}
      />

      {/* the private ring: an app's request for things it is not cleared for
          waits in the inbox, counts beside its tab and says so once in the
          corner (RingWatch, a quiet poll); every surface's "manage access"
          opens one sheet (openAccess, lib/self/ringEvents). Beta: founders
          and testers only; for anyone else nothing is read. */}
      <RingWatch enabled={canSee(F.privateRing, viewer) && canSee(F.nettiInbox, viewer)} />
      <AccessSheetHost enabled={canSee(F.privateRing, viewer)} />

      {/* the gentle post-login invitation to put a face on the cell — founders
          for now, once (dismissal persists locally), never during onboarding —
          neither the launch welcome nor a still-running odyssey walk. */}
      {!showOnboarding &&
        data.isAdmin &&
        !data.profile?.avatarUrl &&
        !(data.onboarding && (data.onboarding.stage !== "free" || !data.onboarding.dismissed)) && <AvatarNudge />}

      {/* first-run onboarding — the launch welcome, mounted OVER the shell. */}
      {showOnboarding && (
        <OnboardingFlow userName={userName} onDone={() => setShowOnboarding(false)} />
      )}
    </ReleaseProvider>
  );
}
